/**
 * Poll module for Vuex store.
 *
 * Live voting: each checkbox is toggled and persisted to the server immediately and the
 * grid is pre-filled from the user's existing votes. Votes stay editable until the
 * super-admin closes the poll. The voter's identity is never sent from here — the backend
 * derives it from the verified Firebase token.
 */
import ApiService from '@/services/ApiService';

export default {
  namespaced: true,

  state: () => ({
    activePoll: null,   // { id, title, status, options: [...], selections: [...] }
    selections: {},     // { [recordingId]: { [optionId]: true } } — mirrors the server
    results: null,      // get_poll_results payload (results page)
    resultsError: false, // true when fetching the results page failed
    adminPolls: [],     // list_polls payload (admin tab)
  }),

  getters: {
    activePoll: state => state.activePoll,
    pollOptions: state => (state.activePoll ? state.activePoll.options : []),
    selections: state => state.selections,
    isOptionSelected: state => (recordingId, optionId) =>
      !!(state.selections[recordingId] && state.selections[recordingId][optionId]),
    hasAnySelection: state =>
      Object.values(state.selections).some(opts => Object.values(opts).some(Boolean)),
    results: state => state.results,
    resultsError: state => state.resultsError,
    adminPolls: state => state.adminPolls,
  },

  mutations: {
    SET_ACTIVE_POLL(state, poll) {
      state.activePoll = poll;
    },
    SET_SELECTIONS(state, selections) {
      state.selections = selections;
    },
    SET_SELECTION(state, { recordingId, optionId, value }) {
      const current = { ...(state.selections[recordingId] || {}) };
      if (value) {
        current[optionId] = true;
      } else {
        delete current[optionId];
      }
      // reassign for Vue 2 reactivity
      state.selections = { ...state.selections, [recordingId]: current };
    },
    CLEAR_SELECTIONS(state) {
      state.selections = {};
    },
    SET_RESULTS(state, results) {
      state.results = results;
    },
    SET_RESULTS_ERROR(state, resultsError) {
      state.resultsError = resultsError;
    },
    SET_ADMIN_POLLS(state, polls) {
      state.adminPolls = polls;
    },
  },

  actions: {
    /**
     * Fetch the active poll and seed the local selections from the user's existing votes.
     */
    async fetchActivePoll({ commit }) {
      const poll = await ApiService.getActivePoll();
      commit('SET_ACTIVE_POLL', poll);

      const selections = {};
      if (poll && Array.isArray(poll.selections)) {
        poll.selections.forEach(({ recording_id, option_id }) => {
          if (!selections[recording_id]) selections[recording_id] = {};
          selections[recording_id][option_id] = true;
        });
      }
      commit('SET_SELECTIONS', selections);
      return poll;
    },

    /**
     * Called when the poll toggle is switched on. Loads the active poll (pre-filled). If
     * there's no active poll, flips the toggle back off with a snackbar.
     */
    async enterPollMode({ dispatch }) {
      let poll;
      try {
        poll = await dispatch('fetchActivePoll');
      } catch (err) {
        dispatch('ui/showSnackbar', 'Could not load the poll — please try again', { root: true });
        dispatch('ui/setPollMode', false, { root: true });
        return;
      }
      if (!poll) {
        dispatch('ui/showSnackbar', 'No active poll right now', { root: true });
        dispatch('ui/setPollMode', false, { root: true });
        return;
      }
      // seed which tracks the user has already commented on (filled vs outline icon)
      dispatch('comments/fetchMyCommentedRecordings', null, { root: true });
    },

    /**
     * Toggle a single (recording, option) vote. Optimistically updates the checkbox and
     * persists to the server; reverts and notifies on failure.
     */
    async toggleVote({ state, commit, dispatch }, { recordingId, optionId, checked }) {
      if (!state.activePoll) return;
      const pollId = state.activePoll.id;

      commit('SET_SELECTION', { recordingId, optionId, value: checked });
      try {
        await ApiService.setPollVote(pollId, recordingId, optionId, checked);
      } catch (err) {
        // revert the optimistic change
        commit('SET_SELECTION', { recordingId, optionId, value: !checked });
        const code = err.response && err.response.data && err.response.data.error;
        if (code === 'not_active') {
          dispatch('ui/showSnackbar', 'This poll has closed', { root: true });
          dispatch('ui/setPollMode', false, { root: true });
        } else {
          dispatch('ui/showSnackbar', "Couldn't save your vote — please try again", { root: true });
        }
      }
    },

    // ── Results page ──
    async fetchResults({ commit }, shareHash) {
      commit('SET_RESULTS_ERROR', false);
      try {
        const results = await ApiService.getPollResults(shareHash);
        commit('SET_RESULTS', results);
        return results;
      } catch (err) {
        commit('SET_RESULTS_ERROR', true);
        throw err;
      }
    },

    // ── Admin (super admin only on the backend) ──
    async fetchPolls({ commit }) {
      const polls = await ApiService.listPolls();
      commit('SET_ADMIN_POLLS', polls);
      return polls;
    },

    async createPoll({ dispatch }, { title, options }) {
      const result = await ApiService.createPoll(title, options);
      await dispatch('fetchPolls');
      return result;
    },

    async setPollStatus({ dispatch }, { pollId, status }) {
      const result = await ApiService.setPollStatus(pollId, status);
      await dispatch('fetchPolls');
      return result;
    },

    async createResultsLink({ dispatch }, pollId) {
      const result = await ApiService.createPollResultsLink(pollId);
      await dispatch('fetchPolls');
      return result;
    },

    async deletePoll({ dispatch }, pollId) {
      const result = await ApiService.deletePoll(pollId);
      await dispatch('fetchPolls');
      return result;
    },
  },
};
